import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Crewmembers from "../../../components/Crewmembers";
import Popup from "../../../components/Popup";
import {
  ContentTypeBadge,
  EmptyState,
  IconButton,
  Page,
  StatusBadge,
} from "../../../components/ui";
import {
  DetailHeader,
  DetailSkeleton,
  useStoredDetail,
} from "../../../components/detail";
import { createSeasonGeneralContent } from "../../../store/actions/uploadMediaAction";
import { stripHtml } from "../../../utils/list";
import ContentInfoForm, {
  contentUpdateError,
  exclusivePrice,
  updatedReleaseYear,
} from "./ContentInfoForm";
import MediaUploadCard from "./MediaUploadCard";
import { uploadDirect } from "./mediaUploads";
import "./content-form.css";

/** Form values of a stored TV show / podcast (JW tags ending with `tagSuffix` are hidden). */
export const seriesFormValues = (series, tagSuffix) => ({
  title: series.title || "",
  description: series.description || "",
  status: Boolean(series.status),
  language: series.default_language || "",
  language_code: series.language_code || "",
  release_date: series.release_year
    ? new Date(series.release_year).toISOString().split("T")[0]
    : "",
  genres: (series.genre || []).map((genre) => genre._id),
  seo_tags: series.seo_tags || [],
  jw_tags: (series.jw_tags || []).filter((tag) => !tag.endsWith(tagSuffix)),
  content_type: series.content_type || "default",
  price: series.price ?? 0,
  monetization: Boolean(series.monetization),
  availability: series.availability || "upcoming",
  imageBase64: "",
  bannerUrl: series.thumbnail?.static_thumbnail_url || "",
});

/** Body of the TV show / podcast update request. */
export const seriesUpdatePayload = (values, series, category) => ({
  thumbnailImageBase64: values.imageBase64,
  description: values.description,
  release_year: updatedReleaseYear(values.release_date, series.release_year),
  title: values.title,
  status: values.status,
  category,
  availability: values.availability,
  isThumbnailUpdated: Boolean(values.imageBase64),
  genres: values.genres,
  seo_tags: values.seo_tags,
  jw_tags: values.jw_tags,
  content_type: values.content_type,
  monetization: values.monetization,
  mime_type: "video/mp4",
  default_language: values.language,
  language_code: values.language_code,
  price: exclusivePrice(values),
});

/**
 * Edit page shared by TV shows and podcasts: trailer (when `trailer` is
 * given), general information, seasons and crew.
 *
 * - `idParam`, `load`, `storeKey`, `setInfo`, `pagePath(id)`: see SeriesInfoPage
 * - `category`, `genreType`, `tagSuffix`: type of the series
 * - `update(body, id)`, `loadSeasons(id)`, `deleteSeason(id)`: requests
 * - `crewUrl`: API segment of the crew members
 * - `infoPath(id)`, `episodesPath(id)`, `listPath`: related pages
 * - `trailer`: `{ storeKey, link(id, body), setTrailer }` (TV shows only)
 * - `texts`: `{ title, updated, seasonError }` translation keys
 */
const SeriesEditPage = ({
  idParam,
  load,
  storeKey,
  setInfo,
  pagePath,
  category,
  genreType,
  tagSuffix,
  update,
  loadSeasons,
  deleteSeason,
  crewUrl,
  infoPath,
  episodesPath,
  listPath,
  trailer,
  texts,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const seriesId = useParams()[idParam];
  const { item: series, loading } = useStoredDetail({
    load: () => load(seriesId),
    setInfo,
    storeKey,
    pagePath: pagePath(seriesId),
  });
  const storedTrailer = useSelector(
    (state) => trailer && state.uploadMediaReducer[trailer.storeKey]
  );
  const [formKey, setFormKey] = useState(0);

  const [seasons, setSeasons] = useState([]);
  const [creatingSeason, setCreatingSeason] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState(null);
  const refreshSeasons = () =>
    loadSeasons(seriesId).then((data) => setSeasons(data?.seasons || []));
  useEffect(() => {
    refreshSeasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  const addSeason = async () => {
    setCreatingSeason(true);
    const created = await dispatch(createSeasonGeneralContent(seriesId));
    setCreatingSeason(false);
    if (!created) {
      toast.error(t(texts.seasonError));
      return;
    }
    await refreshSeasons();
    toast.success(t("Season Created Successfully!"));
  };

  const removeSeason = async (season) => {
    try {
      await deleteSeason(season._id);
      toast.success(t("Season Deleted Successfully!"));
    } catch (error) {
      toast.error(t("Error In Deleting Season"));
    }
    refreshSeasons();
  };

  const openEpisodes = () => {
    if (seasons.length > 0) history.push(episodesPath(seriesId));
    else toast.error(t("Please add Season(s) to the show first"));
  };

  const handleUpdate = async (values) => {
    const payload = seriesUpdatePayload(values, series, category);
    const error = contentUpdateError(
      payload,
      Boolean(series.thumbnail?.static_thumbnail_url)
    );
    if (error) {
      toast.error(t(error));
      return;
    }
    const response = await update(payload, series._id);
    if (!response) {
      toast.error(t("Error In General Content"));
      return;
    }
    dispatch(setInfo(await load(series._id)));
    setFormKey((key) => key + 1);
    toast.success(t(texts.updated));
  };

  const uploadTrailer = async (file) => {
    const mediaId = await uploadDirect(
      file,
      {
        title: `${series.title} Trailer`,
        description: `${stripHtml(series.description)} Trailer`,
      },
      { mimeType: file.type }
    );
    if (!mediaId) throw new Error("Trailer upload failed");
    const response = await trailer.link(series._id, {
      trailer_media_id: mediaId,
      type: "trailer",
    });
    dispatch(trailer.setTrailer(response.data));
  };

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }
  if (!series) {
    return (
      <Page>
        <DetailHeader title={t(texts.title)} />
        <EmptyState icon="ri-tv-2-line">
          {t("No data found for the provided ID!")}
        </EmptyState>
      </Page>
    );
  }

  const seasonsCard = (
    <section
      className={`ui-card content-side-card${trailer ? "" : " content-extras-full"}`}
    >
      <div className="content-side-head">
        <h2 className="content-side-title">
          {t("Seasons")} <span className="ui-cell-muted">({seasons.length})</span>
        </h2>
        <button
          type="button"
          className="ui-btn ui-btn-secondary"
          disabled={creatingSeason}
          onClick={addSeason}
        >
          {creatingSeason ? (
            <span className="ad-spinner" aria-hidden="true" />
          ) : (
            <i className="ri-add-line" aria-hidden="true"></i>
          )}
          {t("Add Season")}
        </button>
      </div>
      {seasons.length === 0 ? (
        <p className="ui-hint">{t("No season yet")}</p>
      ) : (
        <ul className="content-seasons">
          {seasons.map((season, index) => (
            <li key={season._id || index}>
              <span>
                <strong>
                  {t("Season")} {index + 1}
                </strong>
                {Array.isArray(season.episodes) && (
                  <span className="ui-cell-muted">
                    {t("episodeCount", { count: season.episodes.length })}
                  </span>
                )}
              </span>
              <IconButton
                icon="ri-delete-bin-line"
                tone="danger"
                label={t("Delete")}
                onClick={() => setSeasonToDelete(season)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <Page>
      <DetailHeader
        title={series.title || t(texts.title)}
        badges={
          <>
            <StatusBadge active={Boolean(series.status)} />
            <ContentTypeBadge type={series.content_type} />
          </>
        }
        actions={
          <>
            <Link to={infoPath(series._id)} className="ui-btn ui-btn-secondary">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {t("View")}
            </Link>
            <button type="button" className="ui-btn" onClick={openEpisodes}>
              <i className="ri-play-list-add-line" aria-hidden="true"></i>
              {t("Add Episode")}
            </button>
          </>
        }
      />

      {trailer && (
        <div className="content-media">
          <MediaUploadCard
            title={t("Trailer")}
            mediaId={storedTrailer?.[0]?.media_id}
            upload={uploadTrailer}
            successKey="Trailer Uploaded Successfully!"
            failureKey="Trailer Uploading Failed!"
          />
          {seasonsCard}
        </div>
      )}

      <h2 className="content-section-title">{t("General information")}</h2>
      <ContentInfoForm
        key={`${series._id}-${formKey}`}
        genreType={genreType}
        initialValues={seriesFormValues(series, tagSuffix)}
        validate={() => null}
        onSubmit={handleUpdate}
        submitLabel={t("Update")}
        cancelPath={listPath}
        canRelease
      />

      <div className="content-extras">
        {!trailer && seasonsCard}
        <section className="ui-card content-side-card content-extras-full">
          <h2 className="content-side-title">{t("add crew mem")}</h2>
          <Crewmembers
            generalContentObject={[series]}
            url={crewUrl}
            getContent={load}
            getContentGC={setInfo}
          />
        </section>
      </div>

      {seasonToDelete && (
        <Popup
          open={true}
          setOpen={() => setSeasonToDelete(null)}
          deleteHandle={() => removeSeason(seasonToDelete)}
        >
          <div className="model-wrapper">
            {t("Are you sure you want to delete this season?")}
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default SeriesEditPage;
